/** Censor lexicon CRUD + censor preview (WS2 routes). Masking changes caption text only, never audio. Adding,
 * toggling and deleting terms need the `admin` scope (read-only otherwise); the preview is open to every reader. */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useAction } from "../lib/actions";
import { fmtWhen } from "../lib/time";
import { ActionError, ConfirmButton, QueryView, Section, TableScroll } from "../lib/ui";
import { AdminOnlyNote } from "./AdminOnly";

type Lang = Schemas["LexiconIn"]["lang"];
const LANGS: NonNullable<Lang>[] = ["hu", "en", "other"];

function CensorPreview() {
  const [text, setText] = useState("");
  const preview = useAction(
    (t: string) => unwrap(api.POST("/api/censor/preview", { body: { text: t } })),
    { label: "Censor preview", success: (r) => `Censor preview: ${r.hits.length} word${r.hits.length === 1 ? "" : "s"} masked` },
  );
  const [result, setResult] = useState<Schemas["CensorOut"] | null>(null);
  return (
    <form className="ws7-form" aria-label="Censor preview" onSubmit={async (e) => {
      e.preventDefault();
      const r = await preview.run(text);
      if (r) setResult(r);
    }}>
      <label className="ws7-field">
        Text to mask
        <textarea value={text} data-testid="censor-text" onChange={(e) => setText(e.target.value)} />
      </label>
      <div className="ws7-row">
        <button type="submit" data-testid="censor-preview" disabled={preview.pending || !text}>Preview masking</button>
      </div>
      <ActionError error={preview.error} />
      {result && (
        <div data-testid="censor-result">
          <h3>Masked</h3>
          <pre className="ws7-pre" tabIndex={0}>{result.masked}</pre>
          {result.hits.length > 0 && (
            <table className="ws7-table" aria-label="Masked words">
              <thead><tr><th scope="col">Original</th><th scope="col">Term</th><th scope="col">Position</th></tr></thead>
              <tbody>
                {result.hits.map((h) => (
                  <tr key={`${h.start}-${h.end}`}><td>{h.original}</td><td>{h.term}</td><td>{h.start}–{h.end}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </form>
  );
}

export function LexiconTab({ admin }: { admin: boolean }) {
  const lex = useQuery({ queryKey: qk.list("lexicon"), queryFn: () => unwrap(api.GET("/api/lexicon")) });
  const [term, setTerm] = useState("");
  const [lang, setLang] = useState<NonNullable<Lang>>("hu");
  const [pattern, setPattern] = useState("");
  const invalidate = [qk.list("lexicon")];
  const add = useAction(
    () => unwrap(api.POST("/api/lexicon", { body: { term: term.trim(), lang, pattern: pattern.trim() || null, enabled: true } })),
    { label: "Add term", success: (r) => `Added lexicon term ${r.term} (${r.lang})`, invalidate },
  );
  const toggle = useAction(
    (t: Schemas["LexiconOut"]) => unwrap(api.PATCH("/api/lexicon/{term_id}", { params: { path: { term_id: t.id } }, body: { enabled: !t.enabled } })),
    { label: (t) => `Toggle term ${t.term}`, success: (r) => `${r.enabled ? "Enabled" : "Disabled"} lexicon term ${r.term}`, invalidate },
  );
  const del = useAction(
    (t: Schemas["LexiconOut"]) => unwrap(api.DELETE("/api/lexicon/{term_id}", { params: { path: { term_id: t.id } } })),
    { label: (t) => `Delete term ${t.term}`, success: (_r, t) => `Deleted lexicon term ${t.term}`, invalidate },
  );
  return (
    <div className="ws7-page">
      <Section id="lexicon" title="Censor lexicon">
        {!admin && <AdminOnlyNote what="adding, enabling, disabling and deleting terms" testId="lexicon-admin-only" />}
        {admin && <form className="ws7-toolbar" aria-label="Add lexicon term" onSubmit={async (e) => {
          e.preventDefault();
          if (term.trim() && (await add.run())) {
            setTerm("");
            setPattern("");
          }
        }}>
          <label className="ws7-field">
            Term
            <input value={term} maxLength={60} data-testid="lexicon-term" onChange={(e) => setTerm(e.target.value)} />
          </label>
          <label className="ws7-field">
            Language
            <select value={lang} data-testid="lexicon-lang" onChange={(e) => setLang(e.target.value as NonNullable<Lang>)}>
              {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
          <label className="ws7-field">
            Pattern (regex, optional)
            <input value={pattern} maxLength={300} data-testid="lexicon-pattern" onChange={(e) => setPattern(e.target.value)} />
          </label>
          <button type="submit" className="ws7-primary" data-testid="lexicon-add" disabled={!term.trim() || add.pending}>Add term</button>
        </form>}
        <ActionError error={add.error ?? toggle.error ?? del.error} />
        <QueryView q={lex} what="lexicon">
          {(rows) => (
            <TableScroll label="Lexicon table">
              <table className="ws7-table" aria-label="Lexicon" data-testid="lexicon-table">
                <thead>
                  <tr>
                    <th scope="col">Term</th><th scope="col">Lang</th><th scope="col">Pattern</th><th scope="col">Enabled</th>
                    <th scope="col">Added</th>{admin && <th scope="col">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id} data-testid={`lexicon-row-${t.id}`}>
                      <td>{t.term}</td>
                      <td>{t.lang}</td>
                      <td className="ws7-mono">{t.pattern}</td>
                      <td>{t.enabled ? "yes" : "no"}</td>
                      <td>{t.created_by ?? ""} {fmtWhen(t.created_at)}</td>
                      {admin && <td>
                        <span className="ws7-inline">
                          <button type="button" data-testid={`lexicon-toggle-${t.id}`} aria-label={`${t.enabled ? "Disable" : "Enable"} term ${t.term}`}
                            disabled={toggle.pending} onClick={() => void toggle.run(t)}>
                            {t.enabled ? "Disable" : "Enable"}
                          </button>
                          <ConfirmButton label={`Delete ${t.term}`} confirmLabel={`Confirm delete ${t.term}`} testId={`lexicon-delete-${t.id}`}
                            disabled={del.pending} onConfirm={() => void del.run(t)} />
                        </span>
                      </td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </QueryView>
      </Section>
      <Section id="censor" title="Censor preview">
        <CensorPreview />
      </Section>
    </div>
  );
}
